import { useCallback, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _, { uniqBy } from "lodash"

import {
  Q_PARENT_CATEGORY_ID,
  Q_VENUE,
  PROMOTION_TAXONOMY,
  DEFAULT_FILTER_VALUE,
} from "constant"
import dayjs from "config/dayjs"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue/useVenueController"
import { useRecommendedPromotion } from "hooks/sponsored-content/recommended"
import { useGetList } from "venue-react"

export const usePromotion = () => {
  const { data: allPromotions = [] } = useGetList("promotion", {
    populate: true,
  })
  const { venues, isMultiVenueProject, taxonomies = [] } = useVenue()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { recommendedPromotions } = useRecommendedPromotion()

  // Query String filters
  const parentCategoryId = useMemo(
    () => searchParams.get(Q_PARENT_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const promotionCategories = useMemo(
    () =>
      taxonomies?.filter(
        ({ properties }) => properties?.taxonomy === PROMOTION_TAXONOMY
      ) || [],
    [taxonomies]
  )

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/promotions/?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleChangeParentCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_PARENT_CATEGORY_ID, id)
      navigate(`/promotions?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  // Filter by parent category
  const promotionsInParentCategory = useMemo(() => {
    return (
      allPromotions?.filter(({ properties }) => {
        const categories = _(properties).get("local_parent_categories", [])
        const isMatchedCategoryId = _(categories)
          .map("id")
          .includes(parentCategoryId)
        return (
          parentCategoryId === DEFAULT_FILTER_VALUE ||
          (parentCategoryId && isMatchedCategoryId)
        )
      }) || []
    )
  }, [allPromotions, parentCategoryId])

  // Filter by venue
  const promotionsInViewingVenue = useMemo(
    () =>
      allPromotions?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties?.venue_id === venueFilter
      ) || [],
    [allPromotions, venueFilter]
  )

  // Intersection of category and venue filters
  const filteredPromotions = useMemo(
    () =>
      _.intersectionBy(
        promotionsInParentCategory,
        promotionsInViewingVenue,
        "id"
      ),
    [promotionsInParentCategory, promotionsInViewingVenue]
  )

  // Filter recommended promotions by venue
  const filteredRecommendedPromotions = useMemo(
    () =>
      _.intersectionBy(
        recommendedPromotions?.data || [],
        promotionsInViewingVenue,
        "id"
      ),
    [recommendedPromotions?.data, promotionsInViewingVenue]
  )

  // Combine, deduplicate, and sort
  const sanitizedPromotions = useMemo(() => {
    const allCombinedPromotions = [
      ...filteredRecommendedPromotions,
      ...filteredPromotions,
    ]

    const uniquePromotions = uniqBy(allCombinedPromotions, "id")

    return _.orderBy(
      uniquePromotions,
      [
        (item) => (item?.properties?.isAd ? 0 : 1),
        (item) => item?.properties?.order ?? Infinity,
        (item) => dayjs(item?.properties?.start_date).valueOf(),
      ],
      ["asc", "asc", "asc"]
    )
  }, [filteredRecommendedPromotions, filteredPromotions])

  return {
    parentCategoryId,
    promotionCategories,
    promotions: sanitizedPromotions,
    locationVenueId,
    venueFilter,
    venues,
    isMultiVenueProject,
    changeVenueFilter,
    handleChangeParentCategory,
  }
}
